// component/ChannelMembersDrawer.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Avatar,
  Paper,
  Stack,
  CircularProgress,
  TextField,
  Badge,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import Tooltip from "@mui/material/Tooltip";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../api/firebase";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import { ja } from "date-fns/locale";
import { keyframes } from "@mui/system";

const pageSize = 100;

const OnlineBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}));

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`;

const ChannelMembersDrawer = ({ open, onClose, channel, currentUser }) => {
  const [members, setMembers] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef();
  const isLoadingRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Firestoreから追加情報取得
  const fetchUserDetails = async (uids) => {
    const newDetails = {};
    for (const uid of uids) {
      const ref = doc(db, "users", uid);
      const snap = await getDoc(ref);
      if (snap.exists()) newDetails[uid] = snap.data();
    }
    setUserDetails((prev) => ({ ...prev, ...newDetails }));
  };

  const loadMembers = useCallback(async () => {
    if (isLoadingRef.current || !hasMore) return;
    isLoadingRef.current = true;

    try {
      const res = await channel.queryMembers(
        {},
        {},
        {
          offset: page * pageSize,
          limit: pageSize,
        }
      );

      if (res.members.length === 0) {
        setHasMore(false);
        return;
      }

      setMembers((prev) => [...prev, ...res.members]);

      const uids = res.members.map((m) => m.user_id);
      await fetchUserDetails(uids);

      setPage((p) => p + 1);
    } finally {
      isLoadingRef.current = false;
    }
  }, [channel, page, hasMore]);

  useEffect(() => {
    if (open) {
      setMembers([]);
      setUserDetails({});
      setPage(0);
      setHasMore(true);
      isLoadingRef.current = false; // ← これも忘れずに
    }
  }, [open]);

  useEffect(() => {
    if (open && page === 0) loadMembers();
  }, [open, loadMembers]);

  // IntersectionObserverで末尾で再読み込み
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) loadMembers();
    });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [loaderRef, hasMore, loadMembers]);

  const getBloodColor = (type) => {
    switch (type) {
      case "A":
        return { bg: "#e3f2fd", color: "#1565c0" };
      case "B":
        return { bg: "#e8f5e9", color: "#2e7d32" };
      case "O":
        return { bg: "#fff3e0", color: "#ef6c00" };
      case "AB":
        return { bg: "#f3e5f5", color: "#6a1b9a" };
      default:
        return { bg: "#eeeeee", color: "#424242" };
    }
  };

  const filteredMembers = members.filter((member) => {
    const user = member.user;
    const detail = userDetails[member.user_id] || {};
    const name = user.name || "";
    const prefecture = detail.prefecture || "";
    return name.includes(searchTerm) || prefecture.includes(searchTerm);
  });

  const rolePriority = {
    admin: 1,
    owner: 2,
    member: 3,
    user: 4,
  };

  filteredMembers.sort((a, b) => {
    const roleA = rolePriority[a.role] || 99;
    const roleB = rolePriority[b.role] || 99;

    if (roleA !== roleB) {
      return roleA - roleB;
    }

    // 同じロール内では名前順
    return (a.user.name || "").localeCompare(b.user.name || "");
  });


  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 340,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* 固定ヘッダー */}
        <Box
          sx={{
            backgroundColor: "#fff",
            p: 2,
            borderBottom: "1px solid #eee",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6">メンバー一覧</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <TextField
            variant="outlined"
            size="small"
            placeholder="名前または居住地で検索"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
          />
        </Box>

        <Box
          sx={{
            overflowY: "auto",
            flexGrow: 1,
            p: 2,
          }}
        >
          {filteredMembers.map((member, index) => {
            const user = member.user;
            const detail = userDetails[member.user_id] || {};
            const joinedAt = member.created_at
              ? formatDistanceToNow(new Date(member.created_at), {
                  addSuffix: true,
                  locale: ja,
                })
              : "参加日不明";
            const { bg, color } = getBloodColor(detail.bloodtype);

            return (
              <Paper
                key={member.user_id}
                sx={{
                  p: 2,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  backgroundColor: user.online ? "#e8f5e9" : "#fff",
                  animation: user.online ? `${pulse} 2s infinite` : "none",
                  border: user.online ? "1px solid #81c784" : "1px solid #ddd",
                  transition: "all 0.3s ease-in-out",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minWidth: 56,
                  }}
                >
                  {user.online ? (
                    <OnlineBadge
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      variant="dot"
                    >
                      <Avatar src={user.image} alt={user.name} />
                    </OnlineBadge>
                  ) : (
                    <Avatar src={user.image} alt={user.name} />
                  )}

                  {/* ロール表記 */}
                  {currentUser?.isAdmin && (
                    <Tooltip title={member.role} arrow>
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.5,
                          fontSize: "0.65rem",
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {(() => {
                          switch (member.role) {
                            case "admin":
                              return "👑 管理者";
                            case "owner":
                              return "⭐ オーナー";
                            case "member":
                              return "👥 メンバー";
                            case "user":
                            default:
                              return "👤 ユーザー";
                          }
                        })()}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: "bold", maxWidth: 200 }}>
                    {index + 1}. {user.name}
                    {/* ロールごとにアイコン分岐 */}
                    {/* {member.role === "admin" && (
                      <Tooltip title="管理者">
                        <StarIcon sx={{ fontSize: 18, ml: 1, color: "gold" }} />
                      </Tooltip>
                    )}
                    {member.role === "owner" && (
                      <Tooltip title="オーナー">
                        <VerifiedUserIcon
                          sx={{ fontSize: 18, ml: 1, color: "#2196f3" }}
                        />
                      </Tooltip>
                    )}
                    {member.role === "member" && (
                      <Tooltip title="メンバー">
                        <GroupIcon
                          sx={{ fontSize: 18, ml: 1, color: "#4caf50" }}
                        />
                      </Tooltip>
                    )}
                    {member.role === "user" && (
                      <Tooltip title="一般ユーザー">
                        <PersonIcon
                          sx={{ fontSize: 18, ml: 1, color: "#9e9e9e" }}
                        />
                      </Tooltip>
                    )} */}
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {joinedAt}
                    </Typography>
                    {user.last_active && (
                      <Typography variant="caption" color="text.secondary">
                        最終アクティブ：
                        {formatDistanceToNow(new Date(user.last_active), {
                          addSuffix: true,
                          locale: ja,
                        })}
                      </Typography>
                    )}
                  </Stack>

                  <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
                    {detail.prefecture && (
                      <Typography
                        sx={{
                          backgroundColor: "#e0f7fa",
                          color: "#00796b",
                          px: 1,
                          py: 0.3,
                          fontSize: "0.7rem",
                          borderRadius: "999px",
                          fontWeight: "bold",
                        }}
                      >
                        {detail.prefecture.replace(/(県|府|都)$/, "")}
                      </Typography>
                    )}
                    {detail.bloodtype && (
                      <Typography
                        sx={{
                          backgroundColor: bg,
                          color,
                          px: 1,
                          py: 0.3,
                          fontSize: "0.7rem",
                          borderRadius: "999px",
                          fontWeight: "bold",
                        }}
                      >
                        {detail.bloodtype}型
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Paper>
            );
          })}
          {filteredMembers.length === 0 && (
            <Typography variant="body2" align="center" sx={{ mt: 2 }}>
              一致するメンバーが見つかりません
            </Typography>
          )}
          {hasMore && (
            <Box
              ref={loaderRef}
              sx={{ display: "flex", justifyContent: "center", py: 2 }}
            >
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ChannelMembersDrawer;
